import { FishingReport, TechniqueFull, WaterbodyTechnique } from '@omniafishing/core';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiV1 } from '../../../lib/api';
import { getTechniques } from '../../../redux/reference_data';

interface Params {
  selectedSeasonSpeciesFishingReports: FishingReport[];
  selectedSpeciesName: string;
  selectedSeasonGroupName: string;
  url_slug: string;
}

const getTopTechniques = (
  waterbodyTechniques: WaterbodyTechnique[],
  allTechniques: TechniqueFull[],
  fishingReports: FishingReport[]
) => {
  let topTechniqueNamesWithFishingReportCounts = {} as Record<string, number>;
  const topWaterbodyTechniques = waterbodyTechniques.slice(0, 3);

  // generate techniques based on fishing reports
  if (fishingReports.length > 0) {
    topTechniqueNamesWithFishingReportCounts = fishingReports.reduce<Record<string, number>>(
      (topTechniqueNamesWithCounts, fishingReport) => {
        const techniqueName = fishingReport.technique.name;
        if (topTechniqueNamesWithCounts[techniqueName]) {
          topTechniqueNamesWithCounts[techniqueName] += 1;
        } else {
          topTechniqueNamesWithCounts[techniqueName] = 1;
        }
        return topTechniqueNamesWithCounts;
      },
      {}
    );
  }

  // add techniques from top 3 waterbodyTechniques (in case a lake has little or no FR's)
  topWaterbodyTechniques.forEach((waterbodyTechnique) => {
    const technique = allTechniques.find((t) => t.name === waterbodyTechnique.technique);
    if (topTechniqueNamesWithFishingReportCounts[technique.name] == null) {
      topTechniqueNamesWithFishingReportCounts[technique.name] = 0;
    }
  });

  // get techniqueFulls from technique names
  const topTechniqueFulls = Object.keys(topTechniqueNamesWithFishingReportCounts).map(
    (technique) => {
      return allTechniques.find((t) => t.name === technique);
    }
  );

  const topTechniqueFullsWithCounts = topTechniqueFulls.map((technique) => {
    return {
      ...technique,
      reportCount: topTechniqueNamesWithFishingReportCounts[technique.name],
    };
  });

  const topWaterbodyTechniquesDisplayNames = topWaterbodyTechniques.map((ws) => {
    const thisTechnique = allTechniques.find((s) => s.name === ws.technique);
    return thisTechnique.display_name;
  });

  const waterbodyFishingReportTechniquesWithCountsSorted = _.orderBy(
    topTechniqueFullsWithCounts,
    [
      'reportCount',
      (technique) => {
        return topWaterbodyTechniquesDisplayNames.includes(technique.display_name)
          ? _.indexOf(topWaterbodyTechniquesDisplayNames, technique.display_name)
          : Infinity;
      },
      'display_name',
    ],
    ['desc', 'asc', 'asc']
  );

  const totalWaterbodyTechniques = topTechniqueFulls?.length;

  return {
    waterbodyFishingReportTechniquesWithCountsSorted,
    totalWaterbodyTechniques,
  };
};

export const useWaterbodyTechniques = (params: Params) => {
  const {
    selectedSeasonSpeciesFishingReports,
    selectedSpeciesName,
    selectedSeasonGroupName,
    url_slug,
  } = params;

  const allTechniques = useSelector(getTechniques);
  const [waterbodyTechniques, setWaterbodyTechniques] = useState<WaterbodyTechnique[]>([]);

  useEffect(() => {
    async function waterbodyTechniquesFetch() {
      const res = await apiV1.waterbodyTechniquesFetch(url_slug, {
        species: selectedSpeciesName,
        season_group: selectedSeasonGroupName,
      });
      setWaterbodyTechniques(res.data.data);
    }
    waterbodyTechniquesFetch();
  }, [selectedSpeciesName, selectedSeasonGroupName]);

  const { waterbodyFishingReportTechniquesWithCountsSorted, totalWaterbodyTechniques } = useMemo(
    () => getTopTechniques(waterbodyTechniques, allTechniques, selectedSeasonSpeciesFishingReports),
    [
      selectedSeasonSpeciesFishingReports.map((fr) => fr.id).join(','),
      waterbodyTechniques.map((wt) => wt.id).join(','),
    ]
  );
  const topTechniqueName = waterbodyFishingReportTechniquesWithCountsSorted[0]?.name;

  return {
    waterbodyFishingReportTechniquesWithCountsSorted,
    topTechniqueName,
    totalWaterbodyTechniques,
  };
};
